import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Button, Row, Col, InputGroup } from "react-bootstrap";
import { BsCalendar2Check } from "react-icons/bs";
import ApiService from "../../services/ApiService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Select, { components } from "react-select";
import countries from "world-countries";
import { MultiSelect } from "react-multi-select-component";
import ImageUpload from "../../components/ImageUpload";
import { useDropzone } from "react-dropzone";
import plus from "../../assets/images/plus.png";
import { Plus } from "lucide-react";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { useUser } from "../../context/UserContext";
import flag from "../../assets/images/UAE flag.jpeg";
import { bankOptions } from "../../pages/Talent/uae_banks";
import languageOptions from "../../pages/Talent/languageOptions";
import { BsFileEarmarkPdfFill } from "react-icons/bs";
import { useTranslation } from "react-i18next";

// Placeholder ImageDropzone1 component
const ImageDropzone1 = ({ onUpload }) => {
  const [images, setImages] = useState([]);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".webp"] },
    maxSize: 5 * 1024 * 1024, // 5MB
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        fileRejections.forEach((rejection) => {
          rejection.errors.forEach((err) => {
            if (err.code === "file-too-large") {
              toast.error(`File ${rejection.file.name} is too large. Max size is 5MB.`);
            } else if (err.code === "file-invalid-type") {
              toast.error(`File ${rejection.file.name} has invalid type. Supported formats: JPG, JPEG, PNG, WEBP.`);
            } else {
              toast.error(err.message);
            }
          });
        });
      }

      const newImages = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );

      const updatedImages = [...images, ...newImages];

      setImages(updatedImages);
      onUpload(updatedImages); // Pass updated images to parent
    },
    maxFiles: 10, // Set your limit here
  });

  // Cleanup memory (prevent memory leaks)
  useEffect(() => {
    return () => {
      images.forEach((file) => URL.revokeObjectURL(file.preview));
    };
  }, [images]);

  const removeImage = (file) => {
    const updated = images.filter((img) => img !== file);
    setImages(updated);
    onUpload(updated); // Update parent with new list
  };

  return (
    <div>
      {/* Dropzone Box */}
      <div
        {...getRootProps()}
        className={`border p-3 mb-5 text-center rounded-2 ${
          isDragActive ? "bg-light" : ""
        }`}
        style={{ cursor: "pointer" }}
      >
        <input {...getInputProps()} />
        <img
          src={plus}
          style={{
            padding: "20px",
            background: "#F1F1F1",
            borderRadius: "50%",
            width: "60px",
          }}
        />
        <p className="inter-font mt-3">{t("new_text_81")}</p>
        <button
          className="btn text-white inter-font"
          style={{
            background: "#522A30",
            borderRadius: "15px",
            height: "44px",
            width: "107px",
            fontSize: "12px",
          }}
        >
          {t("new_text_82")}
        </button>
      </div>

      {/* Image Previews */}
      <div className="d-flex flex-wrap mt-3 gap-2">
        {images.map((file, index) => (
          <div
            key={index}
            className="position-relative border rounded"
            style={{
              width: "80px",
              height: "80px",
              overflow: "hidden",
            }}
          >
            <img
              src={file.preview}
              alt="preview"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            {/* Remove Button */}
            <button
              type="button"
              onClick={() => removeImage(file)}
              className="btn btn-sm btn-danger rounded-circle position-absolute"
              style={{
                top: "5px",
                right: "5px",
                width: "20px",
                height: "20px",
                padding: "0",
                fontSize: "12px",
                lineHeight: "18px",
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const VideoDropzone = ({ onUpload }) => {
  const [videos, setVideos] = useState([]);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "video/*": [".mp4", ".mov"] },
    maxFiles: 10, // Adjust the limit as needed
    onDrop: (acceptedFiles) => {
      const newVideos = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );
      const updatedVideos = [...videos, ...newVideos];
      setVideos(updatedVideos);
      onUpload(updatedVideos);
    },
  });

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      videos.forEach((file) => URL.revokeObjectURL(file.preview));
    };
  }, [videos]);

  const removeVideo = (fileToRemove) => {
    const updated = videos.filter((file) => file !== fileToRemove);
    setVideos(updated);
    onUpload(updated);
    URL.revokeObjectURL(fileToRemove.preview);
  };

  return (
    <div>
      {/* Dropzone Box */}
      <div
        {...getRootProps()}
        className={`border p-3 text-center rounded-2 ${
          isDragActive ? "bg-light" : ""
        }`}
        style={{ cursor: "pointer" }}
      >
        <input {...getInputProps()} />
        <img
          src={plus}
          style={{
            padding: "20px",
            background: "#F1F1F1",
            borderRadius: "50%",
            width: "60px",
          }}
        />
        <p className="inter-font mt-3">{t("new_text_83")}</p>
        <button
          className="btn text-white inter-font"
          style={{
            background: "#522A30",
            borderRadius: "15px",
            height: "44px",
            width: "107px",
            fontSize: "12px",
          }}
        >
          {t("new_text_82")}
        </button>
      </div>

      {/* Video Previews */}
      <div className="d-flex flex-wrap mt-3 gap-2">
        {videos.map((file, index) => (
          <div
            key={index}
            className="position-relative border rounded"
            style={{
              width: "150px",
              height: "100px",
              overflow: "hidden",
            }}
          >
            <video
              src={file.preview}
              controls
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />

            {/* Remove Button */}
            <button
              type="button"
              onClick={() => removeVideo(file)}
              className="btn btn-danger btn-sm rounded-circle position-absolute"
              style={{
                top: "5px",
                right: "5px",
                width: "24px",
                height: "24px",
                padding: "0",
                fontSize: "14px",
                lineHeight: "20px",
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ResumeDropzone = ({ onUpload, existingResume }) => {
  const [resumeFile, setResumeFile] = useState(null);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  useEffect(() => {
    if (existingResume) {
      setResumeFile({ preview: existingResume, isExisting: true });
    }
  }, [existingResume]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".jpeg", ".jpg", ".png"],
    },
    maxSize: 5 * 1024 * 1024,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        fileRejections.forEach((rejection) => {
          rejection.errors.forEach((err) => {
            if (err.code === "file-too-large") {
              toast.error("File size exceeds 5MB limit.");
            } else if (err.code === "file-invalid-type") {
              toast.error("Invalid file type. Supported formats: PDF, JPG, PNG.");
            } else {
              toast.error(err.message);
            }
          });
        });
      }

      const file = acceptedFiles[0];
      if (file) {
        const newFile = Object.assign(file, {
          preview: URL.createObjectURL(file),
          isExisting: false,
        });
        setResumeFile(newFile);
        onUpload([newFile]);
      }
    },
    multiple: false,
  });

  return (
    <div
      {...getRootProps()}
      className={`border rounded-2 text-center p-3 d-flex flex-column justify-content-center align-items-center
        ${isDragActive ? "bg-light" : ""}`}
      style={{
        border: "1px dashed #ccc",
        height: "193px", 
        cursor: "pointer",
      }}
    >
      <input {...getInputProps()} />

      {resumeFile ? (
        <>
        
          <p
            className="inter-font mb-3 fw-semibold"
            style={{
              fontSize: "14px",
              color: "#333",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              window.open(resumeFile.preview, "_blank");
            }}
          >
            {t("new_text_85")}
          </p>

          <button
            type="button"
            className="btn text-white inter-font"
            style={{
              background: "#522A30",
              borderRadius: "15px",
              height: "44px",
              width: "130px",
              fontSize: "13px",
            }}
          >
            {t("new_text_84")}
          </button>
        </>
      ) : (
        <>
          <img
            src="/icons/plus.svg"
            alt="plus"
            style={{
              padding: "20px",
              background: "#F1F1F1",
              borderRadius: "50%",
              width: "60px",
            }}
          />
          <p
            className="inter-font mt-3 mb-2"
            style={{ fontSize: "14px", color: "#666" }}
          >
            {t("upload_resume")}
          </p>
          <button
            type="button"
            className="btn text-white inter-font"
            style={{
              background: "#522A30",
              borderRadius: "15px",
              height: "44px",
              width: "130px",
              fontSize: "13px",
            }}
          >
            {t("new_text_82")}
          </button>
        </>
      )}
    </div>
  );
};

const EditProfile = ({ onBack }) => {
  const navigate = useNavigate();
  const { userType, profileData, fetchProfile, updateProfile } = useUser();

  const [value, setValue] = useState();

  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const countryOptions = countries.map((c) => ({
    value: c.name.common,
    label: c.name.common,
  }));
  const uaeOption = countryOptions.find(
    (c) => c.label === "United Arab Emirates"
  );
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [isLoader, setIsLoader] = useState(false);
  const [errors, setErrors] = useState({});
  const [educationType, setEducationType] = useState("");
  const [educationOther, setEducationOther] = useState("");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country: "",
    nationality: "",
    // location: "",
    about: "",
    experience_years: "",
    experience_months: "",
    education: "",
    subCategory: "",
    category: "",
    active_job: null,
    picture: null,
    photos: null,
    videos: null,
    bank_name: "",
    account_holder: "",
    account_number: "",
    iban_number: "",
    height: "",
    weight: "",
    languages: "",
    social_accounts: {
      instagram: "",
      tiktok: "",
      // twitter: "",
    },
    previous_works: "",
    parents_data: {   
    name: "",
    phone: "",
  },
  });

  // ✅ Load Category & Subcategory Options First
useEffect(() => {
  const fetchCategories = async () => {
    try {
      const response = await ApiService.request({
        method: "GET",
        url: "/getSkills",
        headers: { Accept: "application/json" },
      });

      const skills = response?.data?.data?.skills || [];
      const formattedOptions = skills.map((skill) => ({
        label: skill.title,
        value: skill._id,
      }));

      setCategoryOptions(formattedOptions);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  fetchCategories();
}, []);

// ✅ Handle Setting Category + Subcategory Once Data Ready
useEffect(() => {
  if (!profileData?.talent || categoryOptions.length === 0) return;

  const catOption = categoryOptions.find(
    (opt) => opt.value === profileData?.talent?.skill_id?._id
  );

  setSelectedCategory(catOption || null);

  setFormData((prev) => ({
    ...prev,
    category: profileData?.talent?.skill_id?._id || "",
    subCategory:
      profileData?.talent?.sub_skill_ids?.length > 0
        ? profileData?.talent?.sub_skill_ids.map((s) => s._id)
        : [],
  }));
}, [profileData?.talent, categoryOptions]);

// ✅ Fetch Subcategories Once Category Is Selected
useEffect(() => {
  if (!selectedCategory) {
    setSubcategoryOptions([]);
    setSelectedSubcategory([]);
    return;
  }

  const fetchSubcategories = async () => {
    try {
      const response = await ApiService.request({
        method: "GET",
        url: `/getSubSkills?skill_id=${selectedCategory.value}`,
        headers: { Accept: "application/json" },
      });

      const subcategories = response?.data?.data?.skills || [];
      const formattedSubOptions = subcategories.map((sub) => ({
        label: sub.title,
        value: sub._id,
      }));

      setSubcategoryOptions(formattedSubOptions);

      // ✅ Preselect subcategories after they load
      if (profileData?.talent?.sub_skill_ids?.length > 0) {
        const matchedSub = formattedSubOptions.filter((opt) =>
          profileData.talent.sub_skill_ids.some((s) => s._id === opt.value)
        );
        setSelectedSubcategory(matchedSub);
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  };

  fetchSubcategories();
}, [selectedCategory, profileData?.talent]);


  useEffect(() => {
    if (profileData?.talent) {
      setFormData({
        // name: profileData?.talent?.name || "",
        first_name: profileData?.talent?.first_name || "",
        last_name: profileData?.talent?.last_name || "",
        email: profileData?.talent?.email || "",
        phone: profileData?.talent?.phone || "",
        country: profileData?.talent?.country || "",
        nationality: profileData?.talent?.nationality || "",
        // location: profileData?.talent?.address || "",
        about: profileData?.talent?.about || "",
        experience_years: profileData?.talent?.year_experience || "",
        experience_months: "",
        education: profileData?.talent?.education || "",
        category: profileData?.talent?.skill_id?._id || "",
        subCategory:
          profileData?.talent?.sub_skill_ids?.length > 0
            ? profileData?.talent?.sub_skill_ids.map((s) => s._id)
            : [],

        active_job: profileData?.talent?.active_job || {
          title: "",
          company: "",
          address: "",
          joining_date: "",
        },
        picture: profileData?.talent?.picture || null,
        photos: profileData?.talent?.photos || null,
        videos: profileData?.talent?.videos || null,
        resume: profileData?.talent?.resume || null,
        bank_name: profileData?.talent?.bank_details?.bank_name || "",
        account_holder: profileData?.talent?.bank_details?.account_holder || "",
        account_number: profileData?.talent?.bank_details?.account_number || "",
        iban_number: profileData?.talent?.bank_details?.iban_number || "",
        height: profileData?.talent?.height || "",
        weight: profileData?.talent?.weight || "",
languages: profileData?.talent?.languages
    ? profileData.talent.languages.join(",") // convert array to comma-separated string
    : "",  
          social_accounts: {
          instagram: profileData?.talent?.social_accounts?.instagram || "",
          tiktok: profileData?.talent?.social_accounts?.tiktok || "",
          // twitter: profileData?.talent?.social_accounts?.twitter || "",
        },
        previous_works: profileData?.talent?.previous_works || "",
         parents_data: {   
    name: profileData?.talent?.parents_data?.name || "",
    phone: profileData?.talent?.parents_data?.phone || "",
  },
      });
      setImageUrl(profileData?.talent?.picture || "");
      if (profileData?.talent?.education) {
        const knownValues = [
          i18n.t("education_middle_school"),
          i18n.t("education_high_school"),
          i18n.t("education_diploma"),
          i18n.t("education_bachelor"),
          i18n.t("education_master"),
        ];
        if (knownValues.includes(profileData.talent.education)) {
          setEducationType(profileData.talent.education);
          setEducationOther("");
        } else {
          setEducationType("other");
          setEducationOther(profileData.talent.education);
        }
      }
      // ✅ set category select
      const catOption = categoryOptions.find(
        (opt) => opt.value === profileData?.talent?.skill_id?._id
      );
      setSelectedCategory(catOption || null);

      // ✅ set subcategories (multi-select)
      if (profileData?.talent?.sub_skill_ids?.length > 0) {
        const subOptions = subcategoryOptions.filter((opt) =>
          profileData?.talent?.sub_skill_ids.some((s) => s._id === opt.value)
        );
        setSelectedSubcategory(subOptions || []);
      }
    }
  }, [profileData?.talent]);
  // ✅ Keep formData.subCategory in sync with selectedSubcategory
useEffect(() => {
  setFormData((prev) => ({
    ...prev,
    subCategory: selectedSubcategory?.length > 0
      ? selectedSubcategory.map((opt) => opt.value)
      : [],
  }));
}, [selectedSubcategory]);

  console.log("errors", formData.subCategory);
  // get Profile Data
  // useEffect(() => {
  //   (async () => {
  //     try {
  //       const skillsRes = await ApiService.request({
  //         method: "GET",
  //         url: "/getSkills",
  //       });
  //       const skills = skillsRes.data?.data?.skills || [];
  //       setSubCategoryOptions(
  //         skills.map((s) => ({ value: s._id, label: s.title }))
  //       );
  //       console.log(skills);

  //       const profileRes = await ApiService.request({
  //         method: "GET",
  //         url: "/getProfile",
  //       });
  //       const profile = profileRes.data?.data?.talent;

  //       if (profile) {
  //         console.log("Profile data loaded:", profile); // Debug log
  //         const selectedSkills = profile.skills.map((s) => ( s.skill_id._id));
  //         console.log(selectedSkills);

  //         setFormData((prev) => ({
  //           ...prev,
  //           first_name:profile.first_name ,
  //           last_name: profile.last_name,
  //           email: profile.email || "",
  //           phone: profile.phone || "",
  //           about: profile.about || "",
  //           location: profile.address || "",
  //           country: profile.country || "",
  //           year_experience: profile.year_experience || "",
  //           active_job: profile.active_job || null,
  //           education: profile.education || "",
  //           picture: profile.picture || null,
  //           photos: profile.photos || null,
  //           videos: profile.videos || null,
  //           subCategory: selectedSkills|| [],
  //         }));
  //       }
  //     } catch (error) {
  //       console.error("Error loading profile:", error);
  //       toast.error(
  //         "Failed to load profile: " + (error.message || "Unknown error")
  //       );
  //     }
  //   })();
  // }, []);

  const handleImageUpload = (file) => {
    console.log("Profile picture uploaded:", file); // Debug log
    setFormData((prev) => ({ ...prev, picture: file }));
    if (errors.picture) setErrors((prev) => ({ ...prev, picture: null }));
  };

 const handlePhotoUpload = (files) => {
  const formatted = files.map((file) => ({
    title: file.name,
    file: file,
  }));
  console.log("Photos uploaded:", formatted);
  setFormData((prev) => ({ ...prev, photos: formatted }));
  if (errors.photos) setErrors((prev) => ({ ...prev, photos: null }));
};

const handleVideoUpload = (files) => {
  const formatted = files.map((file) => ({
    title: file.name,
    file: file,
  }));
  console.log("Videos uploaded:", formatted);
  setFormData((prev) => ({ ...prev, videos: formatted }));
  if (errors.videos) setErrors((prev) => ({ ...prev, videos: null }));
};

  const handleTextChange = (e) => {
    const { name, value } = e.target;

    // Check if the field is nested (e.g., "active_job.title")
    if (name.includes(".")) {
      const [parentKey, childKey] = name.split("."); // Split into keys

      setFormData((prev) => ({
        ...prev,
        [parentKey]: {
          ...prev[parentKey], // Preserve other nested fields
          [childKey]: value, // Update the target field
        },
      }));
    } else {
      // Non-nested field (fallback)
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear errors if they exist
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  // const handlePhoneNumberChange = (value, key) => {
  //   console.log("fffififiif --- ", value);
  //   setFormData((prev) => ({ ...prev, [key]: value }));
  //   if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
  // };

  const handleCountryChange = (selectedOption) =>
    setFormData((prev) => ({ ...prev, country: selectedOption }));

  const handleSelectChange = (name, selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      subCategory: selectedOptions.map((o) => o.value),
    }));
    if (errors.subCategory)
      setErrors((prev) => ({ ...prev, subCategory: null }));
  };
  const handleResumeUpload = useCallback((files) => {
    if (files.length > 0) {
      const uploadedFile = files[0];
      setFormData((prev) => ({
        ...prev,
        resume: uploadedFile,
      }));
    }
  }, []);

  // Update Profile Data fun
//   const handleContinue = async () => {
//     console.log("Save Profile button clicked, formData:", formData); // Debug log
//     const required = [
//       "first_name",
//       "last_name",
//       "country",
//       "nationality",
//       "email",
//       "phone",
//       "about",
//       "active_job",
//       "year_experience",
//       "education",
//       "bank_name",
//       "account_holder",
//       "account_number",
//       "iban_number",
//       "category",
//       "subCategory",
//       "height",
//       "weight",
//       "previous_works",
//     ];
//     const errs = {};
//     required.forEach((k) => {
//       if (!formData[k]) errs[k] = "Required";
//     });
//     if (!formData.social_accounts.instagram)
//       errs["social_accounts.instagram"] = "Required";
//     if (!formData.social_accounts.tiktok)
//       errs["social_accounts.tiktok"] = "Required";
//     if (!formData.social_accounts.twitter)
//       errs["social_accounts.twitter"] = "Required";

//     if (!formData.languages || formData.languages.length === 0)
//       errs.languages = "Required";
//     if (!formData.picture) errs.picture = "Picture is required";

//     if (Object.keys(errs).length) {
//       console.log("Validation errors:", errs); // Debug log
//       setErrors(errs);
//       toast.error("Please fill all required fields");
//       return;
//     }

//     try {
//       setIsLoader(true);
//       const payload = new FormData();

//       Object.entries(formData).forEach(([key, value]) => {
//         if (key === "country" || key === "nationality") {
//           payload.append(
//             key,
//             typeof value === "object" ? value?.value || "" : value || ""
//           );
//         }
//         // Languages (array)
//         else if (key === "languages" && Array.isArray(value)) {
//           value.forEach((lang, index) => {
//             payload.append(`languages[${index}]`, lang);
//           });
//         }
//         // Social Accounts (nested)
//         else if (key === "social_accounts") {
//           Object.entries(value || {}).forEach(([subKey, subValue]) => {
//             payload.append(`${key}[${subKey}]`, subValue || "");
//           });
//         } else if (key === "active_job") {
//           Object.entries(value || {}).forEach(([subKey, subValue]) => {
//             payload.append(`${key}[${subKey}]`, subValue || "");
//           });
//         } else if (
//           (key === "picture" || key === "photos" || key === "videos") &&
//           value instanceof File
//         ) {
//           console.log(`Appending file to payload: ${key}`, value);
//           payload.append(key, value);
//         }  else if (
//           [
//             "bank_name",
//             "account_holder",
//             "account_number",
//             "iban_number",
//           ].includes(key)
//         ) {
//           payload.append(`bank_details[${key}]`, value || "");
//         }
//        else if (key === "category") {
//   payload.append("skill_id", value || "");
// } else if (key === "subCategory" && Array.isArray(value)) {
//   const subSkills = value.join(","); 
//   payload.append("sub_skill_ids", subSkills);
// } else {
//           payload.append(key, value || "");
//         }
//       });

//   if (formData.parents_data?.name)
//       payload.append("parents_data[name]", formData.parents_data.name);
//     if (formData.parents_data?.phone)
//       payload.append("parents_data[phone]", formData.parents_data.phone);

//       const res = await ApiService.request({
//         method: "POST",
//         url: "/updateProfile",
//         data: payload,
//         headers: { "Content-Type": "multipart/form-data" },
//       });
//       const data = res.data;

//       if (data.status) {
//         // toast.success(data.message);
//         if (onBack) onBack();
//       } else {
//         console.error("API error:", data.message);
//         toast.error(data.message || "Server returned an error.");
//       }
//     } catch (error) {
//       console.error("Error updating profile:", error);
//       toast.error(
//         "Failed to update profile: " + (error.message || "Unknown error")
//       );
//     } finally {
//       setIsLoader(false);
//     }
//   };
console.log("320203023210", formData.languages)
const handleContinue = async () => {
  console.log("Save Profile button clicked, formData:", formData);

  const required = [
    "first_name",
    "last_name",
    "country",
    "nationality",
    "email",
    "phone",
    "about",
    "active_job",
    "education",
    "bank_name",
    "account_holder",
    "account_number",
    "iban_number",
    "category",
    "subCategory",
    "height",
    "weight",
    "experience_years",
  ];

  const errs = {};
  required.forEach((k) => {
    if (!formData[k]) errs[k] = "Required";
  });

  if (!formData.languages || formData.languages.length === 0)
    errs.languages = "Required";
  if (!formData.picture) errs.picture = "Picture is required";

  if (Object.keys(errs).length) {
    console.log("Validation errors:", errs);
    setErrors(errs);
    toast.error("Please fill all required fields");
    return;
  }

  try {
    setIsLoader(true);

    const payload = new FormData();

    const totalExperienceYears =
      (parseInt(formData.experience_years || "0", 10) || 0) +
      (parseInt(formData.experience_months || "0", 10) || 0) / 12;

    // Append simple fields
     const skipKeys = [
      "parents_data",
      // "languages",
      
    ];
    const computedEducation =
      educationType === "other"
        ? educationOther || ""
        : educationType || formData.education || "";

    Object.entries(formData).forEach(([key, value]) => {
      if (skipKeys.includes(key)) return;
      if (["parents_data", "social_accounts", "active_job", "subCategory", "category", "experience_years", "experience_months"].includes(key)) return;
      if (key === "country" || key === "nationality") {
        payload.append(key, typeof value === "object" ? value?.value || "" : value || "");
      } else if (key === "picture" && value instanceof File) {
        payload.append("picture", value);
      } else if (["bank_name", "account_holder", "account_number", "iban_number"].includes(key)) {
        payload.append(`bank_details[${key}]`, value || "");
      } else if (key === "education") {
        payload.append("education", computedEducation);
      } else {
        payload.append(key, value || "");
      }
    });

    if (totalExperienceYears > 0) {
      payload.append("year_experience", totalExperienceYears);
    }

    // Append social accounts
    Object.entries(formData.social_accounts || {}).forEach(([k, v]) => {
      payload.append(`social_accounts[${k}]`, v || "");
    });

    // Append languages
    // formData.languages.forEach((lang, index) => {
    //   payload.append(`languages[${index}]`, lang);
    // });

    // Append active job
   if (formData.active_job && typeof formData.active_job === "object") {
  // Ensure it's a single object, not an array
  const { title, company, address, joining_date } = formData.active_job;
  if (title) payload.append("active_job[title]", title);
  if (company) payload.append("active_job[company]", company);
  if (address) payload.append("active_job[address]", address);
  if (joining_date) payload.append("active_job[joining_date]", joining_date);
}

    // Append sub skills as comma separated
    if (formData.subCategory && Array.isArray(formData.subCategory)) {
      payload.append("sub_skill_ids", formData.subCategory.join(","));
    }

    // Append category
    if (formData.category) {
      payload.append("skill_id", formData.category);
    }

    // Append parents data
    if (formData.parents_data?.name)
      payload.append("parents_data[name]", formData.parents_data.name);
    if (formData.parents_data?.phone)
      payload.append("parents_data[phone]", formData.parents_data.phone);

    // Append photos
    if (formData.photos && Array.isArray(formData.photos)) {
      formData.photos.forEach((photo, index) => {
        payload.append(`photos[${index}][title]`, photo.title);
        payload.append(`photos[${index}][file]`, photo.file);
      });
    }

    // Append videos
    if (formData.videos && Array.isArray(formData.videos)) {
      formData.videos.forEach((video, index) => {
        payload.append(`videos[${index}][title]`, video.title);
        payload.append(`videos[${index}][file]`, video.file);
      });
    }


    // Make API request
    const res = await ApiService.request({
      method: "POST",
      url: "/updateProfile",
      data: payload,
      headers: { "Content-Type": "multipart/form-data" },
      // timeout: 180000, // 3 minutes for large payloads
    });

    
    const data = res.data;

    if (data.status) {
      toast.success(data.message || "Profile updated successfully!");
      if (onBack) onBack();
    } else {
      console.error("API error:", data.message);
      toast.error(data.message || "Server returned an error.");
    }
  } catch (error) {
    console.error("Error updating profile:", error);
    toast.error(
      "Failed to update profile: " + (error?.response?.data?.message || error.message || "Unknown error")
    );
  } finally {
    setIsLoader(false);
  }
};

  const [imageUrl, setImageUrl] = useState("");

  const fileInputRef = useRef();

  const handleFileClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      handleImageUpload(e.target.files[0]);
      setImageUrl(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleRemoveSkill = (idToRemove) => {
    const updatedSkills = formData.subCategory.filter(
      (id) => id !== idToRemove
    );
    handleSelectChange(
      "subCategory",
      subCategoryOptions.filter((opt) => updatedSkills.includes(opt.value))
    );
  };
  const handlePhoneNumberChange = (e) => {
    const { name, value, countryCode } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      [`${name}_countryCode`]: countryCode, // Store country code separately
    }));

    // Clear error when user types
    setErrors((prev) => ({ ...prev, [name]: null }));
  };
  const [nationalityOptions, setNationalityOptions] = useState([]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await ApiService.request({
          method: "GET",
          url: "/getCountries",
          headers: {
            Accept: "application/json",
            Language: "en",
          },
        });

        const countries = response?.data?.data?.countries || [];

        const formattedOptions = countries.map((country) => ({
          label: country.title,
          value: country.title,
          flag: country.flag,
          nationality: country.nationality,
        }));

        setNationalityOptions(formattedOptions);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCountries();
  }, []);
  // Custom Option component
  const Option = (props) => (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <img
          src={props.data.flag}
          alt={props.data.label}
          width="24"
          height="16"
          style={{ borderRadius: "3px" }}
        />
        <span>{props.data.label}</span>
      </div>
    </components.Option>
  );
  // Custom SingleValue component (selected value)
  const SingleValue = (props) => (
    <components.SingleValue {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <img
          src={props.data.flag}
          alt={props.data.label}
          width="24"
          height="16"
          style={{}}
        />
        <span>{props.data.label}</span>
      </div>
    </components.SingleValue>
  );

  const customStyles = {
    control: (provided) => ({
      ...provided,
      // borderRadius: "10px",
      borderColor: "#ccc",
      // padding: "2px 4px",
      minHeight: "38px",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    option: (provided, state) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
      gap: "10px",
      backgroundColor: state.isSelected ? "#FF6237" : "white",
      color: state.isSelected ? "white" : "black",
      cursor: "pointer",
    }),
    singleValue: (provided) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
      gap: "8px",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#999",
    }),
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    // Special handling for file uploads
    if (type === "file") {
      if (name.startsWith("parents_data.")) {
        const key = name.split(".")[1];
        setFormData((prev) => ({
          ...prev,
          parents_data: {
            ...prev.parents_data,
            [key]: files[0],
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: files[0],
        }));
      }
      return;
    }

    // ✅ Special handling for phone fields
    if (name === "phone") {
      const cleaned = value.replace(/[^0-9]/g, ""); // Keep only digits
      setFormData((prev) => ({
        ...prev,
        [name]: cleaned,
      }));
      return;
    }

    // ✅ Special handling for parent phone
    if (name === "parents_data.phone") {
      const cleaned = value.replace(/[^0-9]/g, ""); // Only digits
      setFormData((prev) => ({
        ...prev,
        parents_data: {
          ...prev.parents_data,
          phone: cleaned,
        },
      }));
      return;
    }
    // Special case for dob
    if (name === "dob") {
      const birthDate = new Date(value);
      const today = new Date();
      let calculatedAge = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        calculatedAge--;
      }
    }

    // Nested parent_data fields
    if (name.startsWith("parents_data.")) {
      const key = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        parents_data: {
          ...prev.parents_data,
          [key]: type === "checkbox" ? checked : value,
        },
      }));
      return;
    }

    let updatedValue = value;
    if (name === "first_name" || name === "last_name") {
      // Allow only letters and spaces
      if (/^[a-zA-Z\s]*$/.test(value)) {
        updatedValue = value;
      } else {
        return; // Ignore invalid characters
      }
    }
    // Default update
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  const mappedBankOptions = bankOptions.map((bank) => ({
    label: bank.name,
    value: bank.short,
  }));



  // useEffect(() => {
  //   const fetchCategories = async () => {
  //     try {
  //       const response = await ApiService.request({
  //         method: "GET",
  //         url: "/getSkills",
  //         headers: { Accept: "application/json" },
  //       });

  //       const skills = response?.data?.data?.skills || [];
  //       const formattedOptions = skills.map((skill) => ({
  //         label: skill.title,
  //         value: skill._id,
  //       }));

  //       setCategoryOptions(formattedOptions);
  //     } catch (error) {
  //       console.error("Error fetching categories:", error);
  //     }
  //   };

  //   fetchCategories();
  // }, []);

  // // ---------------------- FETCH SUBCATEGORIES ----------------------
  // useEffect(() => {
  //   if (!selectedCategory) {
  //     setSubcategoryOptions([]);
  //     setSelectedSubcategory([]);
  //     return;
  //   }

  //   const fetchSubcategories = async () => {
  //     try {
  //       const response = await ApiService.request({
  //         method: "GET",
  //         url: `/getSubSkills?skill_id=${selectedCategory.value}`,
  //         headers: { Accept: "application/json" },
  //       });

  //       const subcategories = response?.data?.data?.skills || [];
  //       const formattedSubOptions = subcategories.map((sub) => ({
  //         label: sub.title,
  //         value: sub._id,
  //       }));

  //       setSubcategoryOptions(formattedSubOptions);

  //       // ✅ If profileData already has subskills, preselect them
  //       if (profileData?.talent?.sub_skill_ids?.length > 0) {
  //         const matchedSub = formattedSubOptions.filter((opt) =>
  //           profileData.talent.sub_skill_ids.some((s) => s._id === opt.value)
  //         );
  //         setSelectedSubcategory(matchedSub);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching subcategories:", error);
  //     }
  //   };

  //   fetchSubcategories();
  // }, [selectedCategory, profileData?.talent]);
  const [previewImage, setPreviewImage] = useState(
    profileData?.talent?.picture || null
  );
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  return (
  <div
  className="container p-4 p-md-5 bg-white rounded-4 shadow mt-4"
  style={{ maxWidth: "900px", textAlign: isRTL ? "right" : "left" }}
>
  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
    <h4 className="mb-3 mb-md-0 inter-font" style={{ fontSize: "18px" }}>
      {t("edit_personal_info")} {/* Edit Personal Info */}
    </h4>
    <Button
      onClick={handleContinue}
      disabled={isLoader}
      className="d-flex align-items-center border-0 mb-3 inter-font"
      style={{
        background: "#522A30",
        borderRadius: "15px",
        height: "44px",
        width: "107px",
        fontSize: "11px",
      }}
    >
      <BsCalendar2Check className={isRTL ? "ms-2" : "me-2"} />
      {isLoader ? t("saving") : t("new_text_29")} {/* Saving... / Save Profile */}
    </Button>
  </div>

  <Row className="mb-4 g-3">
    {/* Profile Picture */}
    <Col xs={12} sm={4} md={2} className="text-center position-relative">
      <div
        style={{
          width: "90px",
          height: "90px",
          margin: "0 auto",
          position: "relative",
        }}
      >
        <div
          className="rounded-circle bg-dark d-flex align-items-center justify-content-center border overflow-hidden"
          style={{ width: "90px", height: "90px" }}
        >
          {previewImage ? (
            <img
              src={
                typeof previewImage === "string"
                  ? previewImage
                  : URL.createObjectURL(previewImage)
              }
              alt="Profile"
              className="w-100 h-100"
              style={{ objectFit: "cover" }}
            />
          ) : (
            <i
              className="bi bi-person-circle text-white"
              style={{ fontSize: "50px" }}
            ></i>
          )}
        </div>

        <button
          type="button"
          className="btn btn-success btn-sm rounded-circle position-absolute"
          style={{
            top: 0,
            [isRTL ? "left" : "right"]: 0,
            width: "30px",
            height: "30px",
            padding: 0,
            fontSize: "14px",
            lineHeight: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => fileInputRef.current.click()}
        >
          <Plus />
        </button>

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept="image/*"
          onChange={(e) => {
            if (e.target.files[0]) {
              const file = e.target.files[0];
              setFormData((prev) => ({ ...prev, picture: file }));
              setPreviewImage(file); // Update preview immediately
              if (errors.picture)
                setErrors((prev) => ({ ...prev, picture: null }));
            }
          }}
        />
      </div>
    </Col>

    {/* Name and Nationality */}
    <Col xs={12} sm={8} md={5}>
      <label>
        {t("t_d_text8")} <span className="text-danger">*</span> {/* First Name */}
      </label>
      <Form.Control
        type="text"
        placeholder={t("t_d_text9")} 
        name="first_name"
        value={formData.first_name}
        onChange={handleTextChange}
        isInvalid={!!errors.first_name}
        className="mb-3 rounded-2 inter-font custom-input px-3"
        style={{ textAlign: isRTL ? "right" : "left" }}
      />
      <Form.Control.Feedback type="invalid">
        {errors.first_name}
      </Form.Control.Feedback>

      <label>
        {t("t_d_text10")} <span className="text-danger">*</span> {/* Last Name */}
      </label>
      <Form.Control
        type="text"
        placeholder={t("t_d_text11")} 
        name="last_name"
        value={formData.last_name}
        onChange={handleTextChange}
        isInvalid={!!errors.last_name}
        className="mb-3 rounded-2 inter-font custom-input px-3"
        style={{ textAlign: isRTL ? "right" : "left" }}
      />
      <Form.Control.Feedback type="invalid">
        {errors.last_name}
      </Form.Control.Feedback>

      <label>
        {t("country")} <span className="text-danger">*</span> {/* Country */}
      </label>
      <Select
        options={[...nationalityOptions].sort((a, b) =>
          a.label.localeCompare(b.label)
        )}
        styles={customStyles}
        components={{ Option, SingleValue }}
        value={nationalityOptions.find(
          (option) => option.value === formData.country
        )}
        onChange={(selectedOption) =>
          setFormData((prev) => ({
            ...prev,
            country: selectedOption ? selectedOption.value : "",
            nationality: selectedOption ? selectedOption.nationality : "",
          }))
        }
        placeholder={t("select_country")} 
      />
    </Col>

    {/* Contact Info */}
    <Col xs={12} md={5}>
      <label>{t("h_text4")}</label> {/* Email */}
      <Form.Control
        type="email"
        placeholder="example@email.com"
        name="email"
        value={formData.email}
        onChange={handleTextChange}
        isInvalid={!!errors.email}
        className="mb-3 rounded-2 inter-font custom-input px-3"
        disabled
        style={{ textAlign: isRTL ? "right" : "left" }}
      />
      <Form.Control.Feedback type="invalid">
        {errors.email}
      </Form.Control.Feedback>

      <label>{t("phone_number")}</label> {/* Phone Number */}
      <InputGroupWithFlag
        placeholder="50 123 4567"
        name="phone"
        value={formData.phone}
        onChange={handlePhoneNumberChange}
        isInvalid={!!errors.phone}
        disabled={true}
      />
      <Form.Control.Feedback type="invalid">
        {errors.phone}
      </Form.Control.Feedback>
    </Col>
  </Row>

  {/* Bio */}
  <Row className="mb-4">
    <Col xs={12}>
      <label>
        {t("bio")} <span className="text-danger">*</span> {/* Bio */}
      </label>
      <div style={{ position: "relative" }}>
        <Form.Control
          as="textarea"
          rows={4}
          maxLength={400}
          style={{ height: "100px", textAlign: isRTL ? "right" : "left" }}
          name="about"
          value={formData.about}
          onChange={(e) =>
            setFormData({ ...formData, about: e.target.value })
          }
          isInvalid={!!errors.about}
          className="rounded-2 inter-font custom-input"
        />
        <span
          style={{
            position: "absolute",
            bottom: "4px",
            [isRTL ? "left" : "right"]: "15px",
            fontSize: "14px",
            color: "#6c757d",
          }}
        >
          {formData.about.length}/400
        </span>
        <Form.Control.Feedback type="invalid">
          {errors.about}
        </Form.Control.Feedback>
      </div>
    </Col>
  </Row>

  {/* ================= Category & Subcategory ================= */}
  <Row className="mb-4 g-3">
    {/* Category */}
    <Col xs={12} md={6}>
      <label className="">
        {t("t_d_text41")} <span className="text-danger">*</span> 
      </label>
      <Select
        options={[...categoryOptions].sort((a, b) =>
          a.label.localeCompare(b.label)
        )}
        value={selectedCategory}
        onChange={(option) => {
          setSelectedCategory(option);
          setFormData((prev) => ({
            ...prev,
            category: option ? option.value : "",
          }));
        }}
        placeholder={t("new_text_48")} 
        className="inter-font"
        classNamePrefix="react-select"
        // styles={customSelectStyles}
      />
      {errors.category && (
        <small className="text-danger">{errors.category}</small>
      )}
    </Col>

    {/* Subcategory */}
    <Col xs={12} md={6}>
      <label className="">
        {t("t_d_text42")} <span className="text-danger">*</span> {/* Subskill */}
      </label>
      <div>
        <MultiSelect
          options={[...subcategoryOptions].sort((a, b) =>
            a.label.localeCompare(b.label)
          )}
          value={selectedSubcategory || []}
          onChange={(selected) => {
            setSelectedSubcategory(selected);
            setFormData((prev) => ({
              ...prev,
              subCategory: selected.length > 0 ? selected.map(opt => opt.value) : [],
            }));
          }}
          hasSelectAll={false}
          labelledBy={t("select_subskills")}
          overrideStrings={{
            selectSomeItems: t("select_subskills_placeholder"),
            allItemsAreSelected: t("all_items_selected"),
            selectAll: t("select_all"),
            search: t("search"),
          }}
          className="inter-font"
          style={{
            flex: 1,
            border: "none",
            boxShadow: "none",
            backgroundColor: "transparent",
            padding: "0",
          }}
        />
      </div>
      {errors.subCategory && (
        <small className="text-danger">{errors.subCategory}</small>
      )}
    </Col>
  </Row>

  {/* Experience & Education */}
  <Row className="mb-4 g-3">
    <Col xs={12} md={6}>
      <label>
        {t("experience")} <span className="text-danger">*</span>
      </label>
      <div className="d-flex gap-2">
        <Form.Select
          name="experience_years"
          value={formData.experience_years}
          onChange={handleTextChange}
          isInvalid={!!errors.experience_years}
          className="mb-3 rounded-2 inter-font custom-input px-3"
          style={{ textAlign: isRTL ? "right" : "left" }}
        >
          <option value="">{t("select_years")}</option>
          {[...Array(31)].map((_, i) => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          name="experience_months"
          value={formData.experience_months}
          onChange={handleTextChange}
          isInvalid={!!errors.experience_months}
          className="mb-3 rounded-2 inter-font custom-input px-3"
          style={{ textAlign: isRTL ? "right" : "left" }}
        >
          <option value="">{t("select_months")}</option>
          {[...Array(12)].map((_, i) => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </Form.Select>
      </div>
      {(errors.experience_years || errors.experience_months) && (
        <div className="text-danger">
          {errors.experience_years || errors.experience_months}
        </div>
      )}
    </Col>

    <Col xs={12} md={6}>
      <label>
        {t("education")} <span className="text-danger">*</span>
      </label>
      <Form.Select
        name="education_type"
        value={educationType}
        onChange={(e) => {
          const value = e.target.value;
          setEducationType(value);
          if (value !== "other") {
            setEducationOther("");
          }
        }}
        isInvalid={!!errors.education}
        className="mb-3 rounded-2 inter-font custom-input px-3"
        style={{ textAlign: isRTL ? "right" : "left" }}
      >
        <option value="">{t("select_option")}</option>
        <option value={i18n.t("education_middle_school")}>{t("education_middle_school")}</option>
        <option value={i18n.t("education_high_school")}>{t("education_high_school")}</option>
        <option value={i18n.t("education_diploma")}>{t("education_diploma")}</option>
        <option value={i18n.t("education_bachelor")}>{t("education_bachelor")}</option>
        <option value={i18n.t("education_master")}>{t("education_master")}</option>
        <option value="other">{t("education_other")}</option>
      </Form.Select>
      {educationType === "other" && (
        <Form.Control
          type="text"
          placeholder={t("education_other")}
          value={educationOther}
          onChange={(e) => setEducationOther(e.target.value)}
          className="mb-3 rounded-2 inter-font custom-input px-3"
          style={{ textAlign: isRTL ? "right" : "left" }}
        />
      )}
      <Form.Control.Feedback type="invalid">
        {errors.education}
      </Form.Control.Feedback>
    </Col>
  </Row>

  {profileData.talent.parents_data ?
    <>
      <h5 className="my-3 inter-font">{t("parent_details")}</h5> {/* Parent Details */}

      <Row className="mb-4 g-3">
        {/* Parent Name */}
        <Col xs={12} md={6}>
          <label className="">
            {t("t_d_text34")} <span className="text-danger">*</span> {/* Parent Name */}
          </label>
          <Form.Control
            type="text"
            name="parents_data[name]"
            value={formData?.parents_data?.name}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                parents_data: { ...prev.parents_data, name: e.target.value },
              }))
            }
            placeholder={t("t_d_text35")}
            className="rounded-2 inter-font custom-input px-3"
            style={{ backgroundColor: "#f8f8f8", textAlign: isRTL ? "right" : "left" }}
            isInvalid={!!errors["parents_data.name"]}
          />
          <Form.Control.Feedback type="invalid">
            {errors["parents_data.name"]}
          </Form.Control.Feedback>
        </Col>

        {/* Parent Phone */}
        <Col xs={12} md={6}>
          <label className="">
            {t("t_d_text36")} <span className="text-danger">*</span> {/* Parent Phone */}
          </label>
          <InputGroupWithFlag
            placeholder="50 123 4567"
            name="parents_data[phone]"
            value={formData?.parents_data?.phone || ""}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                parents_data: { ...prev.parents_data, phone: e.target.value },
              }))
            }
            isInvalid={!!errors["parents_data.phone"]}
            disabled={true}
          />
          <Form.Control.Feedback type="invalid">
            {errors["parents_data.phone"]}
          </Form.Control.Feedback>
        </Col>
      </Row>
    </>
    : null
  }

  {/* ================= Bank Details ================= */}
  <h5 className="my-3 inter-font">{t("bank_details")}</h5> {/* Bank Details */}
  <Row className="mb-4 g-3">
    {/* Bank Name */}
    <Col xs={12} md={6}>
      <label className="">
        {t("t_d_text26")} <span className="text-danger">*</span> {/* Bank Name */}
      </label>
      <Select
        name="bank_name"
        options={mappedBankOptions}
        value={
          mappedBankOptions.find(
            (option) => option.value === formData.bank_name
          ) || null
        }
        onChange={(selectedOption) =>
          handleChange({
            target: {
              name: "bank_name",
              value: selectedOption ? selectedOption.value : "",
            },
          })
        }
        placeholder={t("select_bank")} 
        className="inter-font"
        classNamePrefix="react-select"
        // styles={customSelectStyles}
      />
      {errors.bank_name && (
        <small className="text-danger">{errors.bank_name}</small>
      )}
    </Col>

    {/* Account Holder */}
    <Col xs={12} md={6}>
      <label className="">
        {t("t_d_text28")} <span className="text-danger">*</span> {/* Account Holder */}
      </label>
      <Form.Control
        type="text"
        name="account_holder"
        value={formData.account_holder}
        onChange={handleChange}
        placeholder={t("t_d_text29")}
        className="rounded-2 inter-font custom-input px-3"
        style={{ backgroundColor: "#f8f8f8", textAlign: isRTL ? "right" : "left" }}
        isInvalid={!!errors.account_holder}
      />
      <Form.Control.Feedback type="invalid">
        {errors.account_holder}
      </Form.Control.Feedback>
    </Col>

    {/* Account Number */}
    <Col xs={12} md={6}>
      <label className="">
        {t("t_d_text30")} <span className="text-danger">*</span> {/* Account Number */}
      </label>
      <Form.Control
        type="text"
        name="account_number"
        value={formData.account_number}
        onChange={handleChange}
        placeholder={t("t_d_text31")} 
        className="rounded-2 inter-font custom-input px-3"
        style={{ backgroundColor: "#f8f8f8", textAlign: isRTL ? "right" : "left" }}
        isInvalid={!!errors.account_number}
      />
      <Form.Control.Feedback type="invalid">
        {errors.account_number}
      </Form.Control.Feedback>
    </Col>

    {/* IBAN Number */}
    <Col xs={12} md={6}>
      <label className="">
        {t("t_d_text32")} <span className="text-danger">*</span> {/* IBAN Number */}
      </label>
      <Form.Control
        type="text"
        name="iban_number"
        value={formData.iban_number}
        onChange={handleChange}
        placeholder={t("t_d_text33")}  
        className="rounded-2 inter-font custom-input px-3"
        style={{ backgroundColor: "#f8f8f8", textAlign: isRTL ? "right" : "left" }}
        isInvalid={!!errors.iban_number}
      />
      <Form.Control.Feedback type="invalid">
        {errors.iban_number}
      </Form.Control.Feedback>
    </Col>
  </Row>

  <h5 className="my-3 inter-font">{t("active_job")}</h5> {/* Active Job */}
  <Row className="mb-3 g-3">
    <Col xs={12} md={6}>
      <label>{t("job_title")}</label> {/* Job Title */}
      <Form.Control
        type="text"
        name="active_job[title]"
        placeholder={t("enter_job_title")}
        value={formData.active_job?.title || ""}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            active_job: { ...prev.active_job, title: e.target.value },
          }))
        }
        style={{ textAlign: isRTL ? "right" : "left" }}
      />
    </Col>

    <Col xs={12} md={6}>
      <label>{t("company")}</label> {/* Company */}
      <Form.Control
        type="text"
        name="active_job[company]"
        placeholder={t("enter_company_name")}
        value={formData.active_job?.company || ""}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            active_job: { ...prev.active_job, company: e.target.value },
          }))
        }
        style={{ textAlign: isRTL ? "right" : "left" }}
      />
    </Col>

    <Col xs={12} md={6}>
      <label>{t("address")}</label> {/* Address */}
      <Form.Control
        type="text"
        name="active_job[address]"
        placeholder={t("enter_company_address")}
        value={formData.active_job?.address || ""}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            active_job: { ...prev.active_job, address: e.target.value },
          }))
        }
        style={{ textAlign: isRTL ? "right" : "left" }}
      />
    </Col>

    <Col xs={12} md={6}>
      <label>{t("joining_date")}</label> {/* Joining Date */}
      <Form.Control
        type="date"
        name="active_job[joining_date]"
        placeholder={t("select_joining_date")}
        value={formData.active_job?.joining_date || ""}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            active_job: {
              ...prev.active_job,
              joining_date: e.target.value,
            },
          }))
        }
        style={{ textAlign: isRTL ? "right" : "left" }}
      />
    </Col>
  </Row>

  <h5 className="my-3 inter-font">{t("other_details")}</h5> {/* Other Details */}

  <Row className="mb-3 g-3">
    {/* Height */}
    <Col xs={12} md={6}>
      <label>
        {t("t_d_text14")} <span className="text-danger">*</span> {/* Height (cm) */}
      </label>
      <Form.Control
        type="number"
        name="height"
        value={formData.height}
        onChange={handleChange}
        placeholder={t("t_d_text14")} 
        className="inter-font rounded-2 custom-input px-3"
        style={{ backgroundColor: "#f8f8f8", textAlign: isRTL ? "right" : "left" }}
        isInvalid={!!errors.height}
      />
      <Form.Control.Feedback type="invalid">
        {errors.height}
      </Form.Control.Feedback>
    </Col>

    {/* Weight */}
    <Col xs={12} md={6}>
      <label>
        {t("t_d_text15")} <span className="text-danger">*</span> {/* Weight (kg) */}
      </label>
      <Form.Control
        type="number"
        name="weight"
        value={formData.weight}
        onChange={handleChange}
        placeholder={t("t_d_text15")} 
        className="inter-font rounded-2 custom-input px-3"
        style={{ backgroundColor: "#f8f8f8", textAlign: isRTL ? "right" : "left" }}
        isInvalid={!!errors.weight}
      />
      <Form.Control.Feedback type="invalid">
        {errors.weight}
      </Form.Control.Feedback>
    </Col>
  </Row>

  {/* Spoken Languages */}
  <Row className="mb-3 g-3">
    <Col xs={12} md={6}>
      <label>
        {t("t_d_text16")} <span className="text-danger">*</span> {/* Spoken Languages */}
      </label>
      <Select
        isMulti
        name="languages"
        options={languageOptions}
        value={languageOptions.filter((opt) =>
          formData.languages.includes(opt.value)
        )}
        onChange={(selectedOptions) => {
          setFormData((prev) => ({
            ...prev,
            languages: selectedOptions ? selectedOptions.map((opt) => opt.value).join(",") : "",
          }));
        }}
        className="basic-multi-select"
        classNamePrefix="select"
        placeholder={t("select_languages")}
      />
      {errors.languages && (
        <small className="text-danger">{errors.languages}</small>
      )}
    </Col>
  </Row>

  <Col xs={12} className="mb-5 g-3">
    <label>
      {t("social_previous_links")}
    </label>
    <Form.Control
      type="text"
      name="previous_works"
      value={formData.previous_works}
      onChange={handleChange}
      placeholder={t("social_previous_links_placeholder")}
      className="inter-font rounded-2 custom-input px-3"
      style={{ backgroundColor: "#f8f8f8", textAlign: isRTL ? "right" : "left" }}
    />
  </Col>

  <Row>
    <Col md={4}>
      <h4 className="inter-font" style={{ fontSize: "18px" }}>
        {t("add_photo")}
      </h4>
      <p
        className="text-muted small"
        style={{ textAlign: isRTL ? "right" : "left", fontSize: "12px" }}
      >
        {t("photo_requirements")}
      </p>
      <ImageDropzone1
        onUpload={(files) => {
          console.log("Uploaded images:", files);
          handlePhotoUpload(files);
        }}
      />
      {errors.photos && (
        <div className="text-danger small mt-1">{errors.photos}</div>
      )}
    </Col>
    <Col md={4}>
      <h4 className="inter-font" style={{ fontSize: "18px" }}>
        {t("upload_video")}
      </h4>
      <p
        className="text-muted small"
        style={{ textAlign: isRTL ? "right" : "left", fontSize: "12px" }}
      >
        {t("video_requirements")}
      </p>
      <VideoDropzone
        onUpload={(files) => {
          console.log("Uploaded videos:", files);
          handleVideoUpload(files);
        }}
      />
      {errors.videos && (
        <div className="text-danger small mt-1">{errors.videos}</div>
      )}
    </Col>
    <Col md={4}>
      <h4 className="inter-font" style={{ fontSize: "18px" }}>
        {t("upload_resume")}
      </h4>
      <p
        className="text-muted small"
        style={{ textAlign: isRTL ? "right" : "left", fontSize: "12px" }}
      >
        {t("resume_requirements")}
      </p>
      <ResumeDropzone
        onUpload={handleResumeUpload}
        existingResume={profileData?.talent?.resume}
      />
      {errors.resume && (
        <div className="text-danger small mt-1">{errors.resume}</div>
      )}
    </Col>
  </Row>
</div>
  );
};
const InputGroupWithFlag = ({
  placeholder,
  name,
  value,
  onChange,
  isInvalid,
  disabled,
}) => {
    const displayValue = value?.startsWith("971") ? value.slice(3) : value || "";

  const handleNumberChange = (e) => {
    // Remove all non-numeric characters
    const numericValue = e.target.value.replace(/[^0-9]/g, "");
    onChange({
      target: {
        name: name,
        value: numericValue,
      },
    });
  };

  return (
    <div
      className={`inter-font d-flex form-control align-items-center border rounded ps-2 ${
        isInvalid ? "border-danger" : ""
      }`}
      style={{ direction: "ltr" }}
    >
      <img
        src={flag}
        alt="UAE"
        style={{ width: "25px", height: "18px", marginRight: "6px" }}
      />
      <span className="inter-font text-muted pe-2 inter-font">(+971)</span>
      <Form.Control
        type="text"
        name={name}
        placeholder={placeholder}
        value={displayValue}
        onChange={handleNumberChange} // <-- updated
        isInvalid={isInvalid}
        maxLength={value?.startsWith("0") ? 10 : 9}
        className="inter-font border-0 ps-1 sofia-font w-100"
        style={{
          boxShadow: "none",
          height: "33px",
        }}
        disabled={disabled}
      />
    </div>
  );
};

export default EditProfile;
